// Source instructions in a modal: opened by a click on a source (landing, /connections,
// /preview) or by a direct link `#<source>` (e.g. /connections#appsflyer), so the skill
// and e-mails can point straight at an instruction.
import { useHydrated } from "../hydrated";
import { ClaudeLinks } from "./ClaudeLinks";
import { useCallback, useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import type { Connections } from "../api";
import { t } from "../i18n";
import { CONN_PROVIDER_NAMES, loginUrl } from "../lib";
import { ChatgptPluginLink } from "./ChatGpt";
import { AppsflyerFigure } from "./AppsflyerFigure";
import { AscFigure } from "./AscFigure";
import { XIcon } from "./icons";
import { SOURCE_ICON, SourceIcon } from "./brand-icons";
import { landingFor } from "../landing";
import { useClientSlug } from "../client";
import { FOCUSABLE, GUIDES, guideAnchor, guideFromHash, guideText, linkify, nextFocusIndex, type ErrorLine, type GuideId } from "../guides";

/** The guide named by the URL hash, and how to open or close one. */
export function useGuide(): { open: GuideId | null; show: (id: GuideId) => void; close: () => void } {
  const location = useLocation();
  const navigate = useNavigate();
  // The build has no hash: a guide anchor opens its modal right after hydration (ADR-0050).
  const hydrated = useHydrated();
  const open = hydrated ? guideFromHash(location.hash) : null;
  const show = useCallback(
    (id: GuideId) => navigate({ pathname: location.pathname, search: location.search, hash: `#${guideAnchor(id)}` }),
    [navigate, location.pathname, location.search],
  );
  const close = useCallback(
    () => navigate({ pathname: location.pathname, search: location.search, hash: "" }, { replace: true }),
    [navigate, location.pathname, location.search],
  );
  return { open, show, close };
}

/** A source name that opens its instruction. */
export function GuideButton({ id, children, className = "linklike" }: { id: GuideId; children: ReactNode; className?: string }) {
  const { show } = useGuide();
  return (
    <button
      type="button"
      className={className}
      aria-haspopup="dialog"
      data-guide={id}
      onClick={() => show(id)}
    >
      {children}
      {/* The name starts with the visible text (label-content-name-mismatch, BL-156), then says what opens. */}
      <span className="sr-only"> - {t("guide.open", { source: guideText(id).title })}</span>
    </button>
  );
}

export function Rich({ text }: { text: string }) {
  return (
    <>
      {linkify(text).map((p, i) =>
        "href" in p ? (
          <a key={i} href={p.href} target="_blank" rel="noreferrer">
            {p.href}
          </a>
        ) : (
          <span key={i}>{p.text}</span>
        ),
      )}
    </>
  );
}

export function ErrorList({ items }: { items: ErrorLine[] }) {
  return (
    <ul className="guide-errors">
      {items.map((e, i) => (
        <li key={i}>
          {e.problem && <strong>{e.problem}</strong>}
          {e.problem && " - "}
          <Rich text={e.fix} />
        </li>
      ))}
    </ul>
  );
}

function GuideAction({ id, signedIn, conns }: { id: GuideId; signedIn: boolean; conns?: Connections | null }) {
  const client = useClientSlug();
  const def = GUIDES[id];
  if (def.page || def.connections) {
    return (
      <Link className="btn btn-soft" to="/connections">
        {t("guide.openConnections")}
      </Link>
    );
  }
  if (def.provider) {
    const provider = def.provider;
    const name = CONN_PROVIDER_NAMES[provider];
    if (!signedIn) {
      const via = provider === "meta" ? "meta" : "google"; // TikTok, Google Play and Apple Ads: first sign in to V-Kit through Google or Meta, as everything else
      return (
        <a className="btn btn-soft" href={loginUrl(via, `/connections#${guideAnchor(id)}`)}>
          {t("guide.signIn", { provider: CONN_PROVIDER_NAMES[via] })}
        </a>
      );
    }
    const authorizeLabel =
      provider === "google_play" ? t("conn.connectGooglePlay") : provider === "apple_ads" ? t("conn.connectAppleAds") : t("guide.authorize", { provider: name });
    const conn = conns?.[provider];
    if (conn && !conn.configured) {
      return (
        <span className="btn" aria-disabled="true">
          {authorizeLabel}
        </span>
      );
    }
    return (
      <a className="btn btn-soft" href={`/auth/${provider}/connect${client ? `?client=${encodeURIComponent(client)}` : ""}`}>
        {conn?.connected ? t("guide.reconnect", { provider: name }) : authorizeLabel}
      </a>
    );
  }
  return (
    <Link className="btn btn-soft" to="/install">
      {t("guide.openInstall")}
    </Link>
  );
}

/**
 * The text of one instruction: what you need, the steps, its own errors (and, for a source, the errors
 * common to all). The single renderer for the modal and the /guide page. `level` is the heading level of
 * the sub-headings, `afterSteps` sits between the steps and the errors (the modal's action buttons).
 */
export function GuideBody({
  id,
  level = 3,
  afterSteps,
  common = true,
}: {
  id: GuideId;
  level?: 3 | 4;
  afterSteps?: ReactNode;
  common?: boolean;
}) {
  const g = guideText(id);
  const H = `h${level}` as "h3" | "h4";
  return (
    <>
      <H>{t("guide.needsTitle")}</H>
      <ul>
        {g.needs.map((l, i) => (
          <li key={i}>
            <Rich text={l} />
          </li>
        ))}
      </ul>

      <H>{t(GUIDES[id].page ? "guide.installStepsTitle" : "guide.stepsTitle")}</H>
      {id === "appsflyer" && <AppsflyerFigure testId="fig-appsflyer-guide" />}
      {id === "app_store_connect" && <AscFigure testId="fig-team-keys-guide" />}
      <ol>
        {g.steps.map((l, i) => (
          <li key={i}>
            <Rich text={l} />
          </li>
        ))}
      </ol>

      {afterSteps}

      <H>{t("guide.errorsTitle")}</H>
      <ErrorList items={g.errors} />

      {common && g.common.length > 0 && (
        <>
          <H>{t("guide.commonTitle")}</H>
          <ErrorList items={g.common} />
        </>
      )}
    </>
  );
}

export function GuideModal({
  id,
  onClose,
  signedIn,
  conns,
}: {
  id: GuideId;
  onClose: () => void;
  signedIn: boolean;
  conns?: Connections | null;
}) {
  const g = guideText(id);
  const more = landingFor(id);
  const titleId = useId();
  const dialog = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // Focus moves into the dialog and comes back to where it was; the page behind does not scroll.
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current?.focus();
    // Esc closes wherever the focus is (also when it fell out to the page body).
    const onEsc = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") closeRef.current();
    };
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = overflow;
      if (previous && typeof previous.focus === "function" && previous.isConnected) previous.focus();
    };
  }, [id]);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== "Tab" || !dialog.current) return;
    const items = Array.from(dialog.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (!items.length) return;
    e.preventDefault();
    const current = items.indexOf(document.activeElement as HTMLElement);
    items[nextFocusIndex(current, items.length, e.shiftKey)]!.focus();
  }

  return (
    <div
      className="modal-backdrop"
      data-testid="guide-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialog}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        data-guide-dialog={id}
      >
        <div className="modal-head">
          <h2 id={titleId} className="modal-title">
            {SOURCE_ICON[id] && (
              <span className="src-icon" aria-hidden="true">
                <SourceIcon id={id} size={20} />
              </span>
            )}
            {g.title}
          </h2>
          <button type="button" className="btn btn-ghost btn-icon modal-close" aria-label={t("guide.close")} onClick={onClose}>
            <XIcon size={18} />
          </button>
        </div>
        <p className="lead-small">{g.intro}</p>
        {more && (
          <p className="guide-more">
            <Link className="text-link" to={more.path} data-testid="guide-more">
              {t("guide.more", { source: g.title })}
            </Link>
          </p>
        )}

        <GuideBody
          id={id}
          afterSteps={
            <>
              <div className="actions">
                {id === "chatgpt" && <ChatgptPluginLink />}
                <GuideAction id={id} signedIn={signedIn} conns={conns} />
              </div>
              {id === "plugin" && <ClaudeLinks />}
            </>
          }
        />

        <p className="hint">
          {t("guide.directLink", { url: `https://marketing-kit.app${GUIDES[id].page ?? "/connections"}#${guideAnchor(id)}` })}
        </p>
      </div>
    </div>
  );
}

/** Renders the modal of the guide named by the URL hash, if any. */
export function GuideHost({ signedIn, conns, skip = [] }: { signedIn: boolean; conns?: Connections | null; skip?: GuideId[] }) {
  const { open, close } = useGuide();
  if (!open || skip.includes(open)) return null;
  return <GuideModal id={open} onClose={close} signedIn={signedIn} conns={conns} />;
}
